import { FC, useEffect, useMemo, useState } from 'react'
import cn from './index.module.css'
import CoursesBanner from '@pages/CoursesCatalog/components/CoursesBanner/CoursesBanner'
import useHorizontalSorting, {
  SortingTab,
} from '@pages/CoursesCatalog/components/HorizontalSorting/useHorizontalSorting'
import HorizontalSorting from '@pages/CoursesCatalog/components/HorizontalSorting/HorizontalSorting'
import FolderList from '@pages/CoursesCatalog/components/FolderList/FolderList'
import CoursesList from '@pages/CoursesCatalog/components/CoursesList/CoursesList'
import { fetchCourses } from './api'
import { useGetSettings } from './hooks/getPlatformSettings'
import CourseEnrollmentModal from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/CourseEnrollmentModal'
import Preloader from '@UI/Preloader'

interface ICatalogCoursesTab {}

type SelectedFolder = { folder?: number; subFolder?: number }

const CatalogCoursesTab: FC<ICatalogCoursesTab> = ({}) => {
  const { checkSettings } = useGetSettings()
  const showCourseSorting = checkSettings('enableSortingOfCoursesInCatalog')
  const [isLoading, setIsLoading] = useState(true)
  const [courses, setCourses] = useState<ICourse[]>([])
  const [folder, setFolder] = useState<SelectedFolder>({})
  const [courseEnroll, setCourseEnroll] = useState<{
    courseId: number
    tariffId: number
  } | null>(null)
  const sortingTabs = useFilteredSortingTabs()
  const sortingState = useHorizontalSorting({ tabs: sortingTabs })

  useEffect(() => {
    sortingState.setTabs(sortingTabs)
  }, [sortingTabs])

  const onSort = (tab: SortingTab) => {
    sortingState.toggleTab(tab.id)
  }

  useEffect(() => {
    async function fetch() {
      if (!folder.folder && !folder.subFolder) {
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      const courses = await fetchCourses({
        folder: folder.folder,
        subFolder: folder.subFolder,
        sorting: sortingState.sorting?.[0],
      })
      setCourses(courses)
      setIsLoading(false)
    }

    fetch()
  }, [folder, sortingState.sorting])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const courseId = params.get('course_id')
    const tariffId = params.get('tariff_id')
    if (courseId && tariffId)
      setCourseEnroll({
        courseId: Number(courseId),
        tariffId: Number(tariffId),
      })
  }, [])

  const onEnrollmentDismiss = () => {
    setCourseEnroll(null)
    window.history.replaceState(null, '', window.location.pathname)
  }

  return (
    <>
      <div className={cn.wrapper}>
        <CoursesBanner />
        {showCourseSorting && sortingTabs?.length > 0 && (
          <HorizontalSorting
            label="Сортировать по:"
            tabs={sortingState.state}
            onClick={onSort}
          />
        )}
        <div className={cn.listLayout}>
          <FolderList
            className={cn.folders}
            selectedFolder={folder.folder}
            selectedSubFolder={folder.subFolder}
            onSelect={(value) => {
              setFolder(value)
            }}
          />
          {isLoading ? (
            <div className={cn.courseListLoadingStub}>
              <Preloader />
            </div>
          ) : (
            <CoursesList courses={courses} />
          )}
        </div>
      </div>
      <CourseEnrollmentModal
        visible={!!courseEnroll}
        courseId={courseEnroll?.courseId ?? 0}
        tariffId={courseEnroll?.tariffId ?? 0}
        onDismiss={onEnrollmentDismiss}
        onSuccess={() => {}}
      />
    </>
  )
}

const useFilteredSortingTabs = (): SortingTab[] => {
  const { checkSettings } = useGetSettings()
  const hidePopularity = checkSettings('sortingCoursesInCatalogByPopularity')
  const hidePrice = checkSettings('sortingCoursesInCatalogByPrice')
  const hideDuration = checkSettings('sortingCoursesInCatalogByDuration')
  const hideDemo = checkSettings('sortingCoursesInCatalogByDemoAccess')

  const tabs = useMemo(() => {
    const tabs = []
    if (hidePopularity)
      tabs.push({ id: 'popularity', label: 'По популярности' })
    if (hidePrice) tabs.push({ id: 'price', label: 'По цене' })
    if (hideDuration) tabs.push({ id: 'duration', label: 'По длительности' })
    if (hideDemo)
      tabs.push({ id: 'demo', label: 'Демо-доступ', withoutOrder: true })
    return tabs
  }, [hidePopularity, hidePrice, hideDuration, hideDemo])

  return tabs
}

export default CatalogCoursesTab
